// Accessible tabs (WAI-ARIA tabs pattern, manual activation with arrow keys).
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";

export interface TabItem<K extends string> {
  id: K;
  label: ReactNode;
  icon?: ReactNode;
}

interface TabsProps<K extends string> {
  items: readonly TabItem<K>[];
  value: K;
  onChange: (id: K) => void;
  label: string;
  orientation?: "horizontal" | "vertical";
  idPrefix?: string;
}

export function Tabs<K extends string>({ items, value, onChange, label, orientation = "horizontal", idPrefix }: TabsProps<K>) {
  const autoId = useId();
  const prefix = idPrefix ?? autoId;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keysNext = orientation === "horizontal" ? ["ArrowRight"] : ["ArrowDown"];
    const keysPrev = orientation === "horizontal" ? ["ArrowLeft"] : ["ArrowUp"];
    const index = items.findIndex((i) => i.id === value);
    let next = -1;
    if (keysNext.includes(e.key)) next = (index + 1) % items.length;
    else if (keysPrev.includes(e.key)) next = (index - 1 + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    if (next >= 0) {
      e.preventDefault();
      const item = items[next]!;
      onChange(item.id);
      refs.current[item.id]?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation={orientation}
      className={`tabs${orientation === "vertical" ? " tabs--vertical" : ""}`}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => (
        <button
          key={item.id}
          ref={(el) => {
            refs.current[item.id] = el;
          }}
          role="tab"
          type="button"
          id={`${prefix}-tab-${item.id}`}
          aria-selected={item.id === value}
          aria-controls={`${prefix}-panel-${item.id}`}
          tabIndex={item.id === value ? 0 : -1}
          className="tab"
          onClick={() => onChange(item.id)}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ idPrefix, id, children, className }: { idPrefix: string; id: string; children: ReactNode; className?: string }) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${id}`}
      aria-labelledby={`${idPrefix}-tab-${id}`}
      className={["tab-panel", className ?? ""].join(" ")}
      tabIndex={0}
    >
      {children}
    </div>
  );
}
